import { useZoomCallSummary } from "../hooks/useZoomCallSummary";
import { formatDateTime } from "../helper/dateFormatter";

interface ZoomCallSummaryPanelProps {
  appointmentId: string;
}

const SkeletonRows = () => (
  <div className="zoom-call-summary">
    {Array.from({ length: 4 }).map((_, index) => (
      <span className="placeholder-wave d-block mb-2" key={index}>
        <span className="placeholder w-100"></span>
      </span>
    ))}
  </div>
);

export const ZoomCallSummaryPanel = ({ appointmentId }: ZoomCallSummaryPanelProps) => {
  const { status, summary, errorMessage } = useZoomCallSummary(appointmentId);

  return (
    <div>
      {(status === "idle" || status === "loading") && <SkeletonRows />}

      {(status === "pending" || status === "processing") && (
        <div className="zoom-call-summary">
          <p className="fs-13 text-muted mb-3">Generating call summary&hellip;</p>
          <SkeletonRows />
        </div>
      )}

      {status === "empty" && (
        <div className="zoom-call-summary">
          <p className="fs-13 text-muted mb-0">
            No call summary is available for this appointment.
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="zoom-call-summary">
          <p className="fs-13 text-danger mb-0">
            {errorMessage ?? "Something went wrong generating this call summary."}
          </p>
        </div>
      )}

      {status === "completed" && summary && (
        <div className="zoom-call-summary">
          {summary.detailedSummary && (
            <div className="mb-3">
              <p className="fs-13 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {summary.detailedSummary}
              </p>
            </div>
          )}

          {summary.suggestedTasks && summary.suggestedTasks.length > 0 && (
            <div className="mb-3">
              <h4 className="fs-13 fw-semibold mb-1">Suggested Follow-up Tasks</h4>
              <ul className="fs-13 ps-3 mb-0">
                {summary.suggestedTasks.map((task) => (
                  <li key={`${task.task}-${task.owner}-${task.context}`} className="mb-2">
                    <div className="fw-semibold">{task.task}</div>
                    <div className="text-muted">
                      {task.owner}
                      {task.context ? ` · ${task.context}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {summary.generatedAt && (
            <div className="fs-11 text-muted">
              Generated {formatDateTime(summary.generatedAt)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
